/**
 * @fileoverview Cookie information banner.
 * @package PlayItNow
 * @module apps/front/components/CookieBanner
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useState, useEffect } from 'react'
import { useTranslation } from '@/contexts/I18nContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCookie, faTimes } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'

const COOKIE_BANNER_KEY = 'playitnow_cookie_banner_accepted'

/**
 * @summary Cookie banner component.
 * @note This is an information banner, not a consent banner, as all cookies used are essential.
 */
export function CookieBanner() {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Check if user has already seen the banner
    const hasSeenBanner = localStorage.getItem(COOKIE_BANNER_KEY)
    if (!hasSeenBanner) {
      setIsVisible(true)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_BANNER_KEY, 'true')
    setIsVisible(false)
  }

  const handleClose = () => {
    localStorage.setItem(COOKIE_BANNER_KEY, 'true')
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <FontAwesomeIcon
              icon={faCookie}
              className="w-6 h-6 text-(--color-primary-600) dark:text-(--color-primary-400)"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('cookieBanner.message')}{' '}
              <Link
                to="/cookies"
                className="text-(--color-primary-600) dark:text-(--color-primary-400) hover:underline font-medium"
              >
                {t('cookieBanner.learnMore')}
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleAccept} className="btn btn-primary btn-sm text-sm px-4 py-2">
              {t('cookieBanner.accept')}
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              aria-label={t('common.close')}
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
